import React, {useCallback, useEffect, useState} from 'react'
import {Box, Button, Grid, Stack, Typography} from "@mui/material"
import AddIcon from '@mui/icons-material/Add'
import {ProjectRequests} from "../../../api/requests/ProjectRequests"
import {RoleCheck} from "../../../library/RoleCheck"
import ProjectCard from "./ProjectCard"
import PageHeader from "../../layouts/PageHeader"
import {useApi} from "../../../hooks/useApi"
import {useNavigate} from "react-router-dom"
import ApiError from "../../../api/ApiError"
import {PagedProjectsJson} from "../../../api/json/PagedResponseJson"
import ScrollPaginator from "../../misc/ScrollPaginator"
import NewProjectModal from "../NewProjectModal"
import ProjectCardSkeleton from "./ProjectCardSkeleton"
import DemoDatasetPicker from "./DemoDatasetPicker"
import {useBreadcrumbs} from "../../layouts/Nav/BreadcrumbsProvider"
import {NAV_LABELS} from "../../../library/NavLabels"

function ProjectsPage() {
    useBreadcrumbs([{label: NAV_LABELS.PROJECTS}])

    const [error, setError] = useState<ApiError>()
    const [loading, setLoading] = useState(false)
    const [showNewProjectModal, setShowNewProjectModal] = useState(false)
    const [pages, setPages] = useState<PagedProjectsJson[]>([])

    const {token, currentUser} = useApi()
    const navigate = useNavigate()

    const loadPage = useCallback((pageNumber: number) => {
        if (loading) return

        setLoading(true)
        ProjectRequests.list(pageNumber)
            .then(response => {
                setPages([...pages, response])
                setError(undefined)
            })
            .catch(setError)
            .finally(() => setLoading(false))
    }, [pages, loading])

    useEffect(() => {
        if (!token || !currentUser) {
            navigate("/login")
        }
    }, [token, currentUser])

    const fab = (
        RoleCheck.atLeastManager()
            ? <Button variant="contained"
                      startIcon={<AddIcon/>}
                      onClick={() => setShowNewProjectModal(true)}>
                Create project
            </Button>
            : undefined
    )

    const projects = pages.flatMap(page => page.content)
    const hasProjects = projects.length > 0

    return (
        <>
            <PageHeader pageTitle="Projects" fab={fab}/>

            <ScrollPaginator pages={pages}
                             loadNextPage={pageNumber => loadPage(pageNumber)}
                             error={error}
                             showInitialSpinner={false}
            >
                {pages.length === 0 && !error &&
                    <Grid container spacing={2.5}>
                        {Array.from({length: 6}).map((_, index) => (
                            <Grid item xs={12} sm={6} lg={4} key={index}>
                                <ProjectCardSkeleton/>
                            </Grid>
                        ))}
                    </Grid>
                }
                {hasProjects &&
                    <Grid container spacing={2.5}>
                        {projects.map(project => (
                            <Grid item xs={12} sm={6} lg={4} key={project.id}>
                                <ProjectCard
                                    project={project}
                                    didCopy={copy => {
                                        if (pages.length > 0) {
                                            setPages(pages => [
                                                {
                                                    ...pages[0],
                                                    content: [copy, ...pages[0].content],
                                                },
                                                ...pages.slice(1),
                                            ]);
                                        }
                                    }}
                                    didRename={updated => {
                                        setPages(pages =>
                                            pages.map(page => ({
                                                ...page,
                                                content: page.content.map(project =>
                                                    project.id === updated.id ? {...project, name: updated.name} : project
                                                ),
                                            }))
                                        );
                                    }}
                                    didDelete={deletedId => {
                                        setPages(pages =>
                                            pages.map(page => ({
                                                ...page,
                                                content: page.content.filter(project => project.id !== deletedId),
                                            }))
                                        )
                                    }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                }
            </ScrollPaginator>

            {pages.length > 0 && !hasProjects &&
                <Stack alignItems="center" spacing={3} sx={{mt: 8}}>
                    <Typography variant="h2" sx={{color: '#999'}}>No projects yet</Typography>
                    <Typography variant="body1" sx={{color: '#bbb'}}>
                        Create your first project to get started, or try a demo dataset.
                    </Typography>
                    {RoleCheck.atLeastManager() &&
                        <Button variant="contained" startIcon={<AddIcon/>} onClick={() => setShowNewProjectModal(true)}>
                            Create project
                        </Button>
                    }
                </Stack>
            }

            <DemoDatasetPicker sx={{mt: 8}}/>

            <NewProjectModal open={showNewProjectModal} setOpen={setShowNewProjectModal}/>
        </>
    )
}

export default ProjectsPage
